import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 7, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using CryptoPulse Pro, you agree to be bound by these Terms of Service. 
              If you do not agree, do not use our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">
              CryptoPulse Pro provides AI-powered Bitcoin price predictions, portfolio tracking, 
              trading tools, and market analytics. Our service is for informational purposes only.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Investment Disclaimer</h2>
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 p-6 rounded-lg mb-4">
              <p className="text-amber-900 dark:text-amber-100 font-semibold mb-2">⚠️ Important Warning</p>
              <ul className="list-disc pl-6 text-amber-800 dark:text-amber-200 space-y-2">
                <li>CryptoPulse Pro is NOT financial advice</li>
                <li>AI predictions are not guaranteed to be accurate</li>
                <li>Cryptocurrency trading involves significant risk</li>
                <li>You may lose all invested capital</li>
                <li>Past performance does not guarantee future results</li>
                <li>We are not registered investment advisors</li>
              </ul>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Always conduct your own research and consult licensed financial advisors before making investment decisions.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. User Responsibilities</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">You agree to:</p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide accurate account information</li>
              <li>Maintain the security of your password</li>
              <li>Accept responsibility for all account activity</li>
              <li>Not share your account with others</li>
              <li>Comply with all applicable laws</li>
              <li>Not use the platform for illegal activities</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Prohibited Activities</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Attempting to hack or disrupt the platform</li>
              <li>Using bots or automated systems (except through official API)</li>
              <li>Scraping or copying platform content</li>
              <li>Sharing copyrighted material without permission</li>
              <li>Impersonating others or providing false information</li>
              <li>Manipulating or gaming the AI prediction system</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Subscription and Payments</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              <strong>Pricing:</strong> Pro ($29/month), Enterprise ($99/month)
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Subscriptions auto-renew monthly unless canceled</li>
              <li>Payments processed securely through Stripe</li>
              <li>You can cancel anytime from your account dashboard</li>
              <li>Refunds available within 14 days (see Refund Policy)</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Refund Policy</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              <strong>14-Day Money-Back Guarantee:</strong>
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Full refund if requested within 14 days of purchase</li>
              <li>Contact support@cryptopulsepro.io to request refund</li>
              <li>Platform licenses (one-time purchases) are non-refundable after download</li>
              <li>Refunds processed within 5-10 business days</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Intellectual Property</h2>
            <p className="text-muted-foreground leading-relaxed">
              All platform content, code, design, and branding are owned by CryptoPulse Pro. 
              You may not copy, reproduce, or redistribute our content without written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>We are not liable for trading losses</li>
              <li>We do not guarantee prediction accuracy</li>
              <li>We are not responsible for third-party services (Stripe, Binance, etc.)</li>
              <li>Maximum liability is limited to amount paid in last 12 months</li>
              <li>We are not liable for indirect, incidental, or consequential damages</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Termination</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may suspend or terminate your account for violations of these terms, 
              illegal activity, or abuse. You may cancel your account anytime.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms. Continued use after changes constitutes acceptance. 
              We will notify users of material changes via email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Contact</h2>
            <div className="bg-muted p-6 rounded-lg">
              <p className="font-semibold mb-2">CryptoPulse Pro</p>
              <p className="text-muted-foreground">Email: support@cryptopulsepro.io</p>
              <p className="text-muted-foreground">Website: https://cryptopulsepro.io</p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <Button onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;